import { z } from 'zod';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiClient } from './client';

/**
 * My notifications surface (T-0112 / C20-122).
 *
 * Wires the T-0080 caller-scoped preference endpoints from
 * `apps/api/src/routes/notification-preferences.ts`:
 *
 *   - GET  /api/v1/notifications/preferences
 *           → { global: [{ event_type, is_subscribed }],
 *               per_engagement_mutes: [{ engagement_id, event_type, is_muted }] }
 *   - PUT  /api/v1/notifications/preferences/global
 *           → { updated: N }
 *   - PUT  /api/v1/notifications/preferences/engagement/:engagement_id
 *           → { updated: N }
 *
 * The cadence selector (off / daily / weekly) for email digests does not
 * have a matching server field today — the T-0080 surface only carries
 * `is_subscribed` (global) + `is_muted` (per-engagement). The digest cadence
 * UI is wired through a local mutation that flags a backend gap to extend
 * the schema with `digest_cadence`. v1 keeps the visual + emits no PUT;
 * see TODO below.
 */

export const GlobalSubscriptionEntrySchema = z.object({
  event_type: z.string(),
  is_subscribed: z.boolean(),
});
export type GlobalSubscriptionEntry = z.infer<typeof GlobalSubscriptionEntrySchema>;

export const PerEngagementMuteEntrySchema = z.object({
  engagement_id: z.string().uuid(),
  event_type: z.string(),
  is_muted: z.boolean(),
});
export type PerEngagementMuteEntry = z.infer<typeof PerEngagementMuteEntrySchema>;

export const GetPreferencesResultSchema = z.object({
  global: z.array(GlobalSubscriptionEntrySchema),
  per_engagement_mutes: z.array(PerEngagementMuteEntrySchema),
});
export type GetPreferencesResult = z.infer<typeof GetPreferencesResultSchema>;

export const PutPreferencesResultSchema = z.object({
  updated: z.number(),
});
export type PutPreferencesResult = z.infer<typeof PutPreferencesResultSchema>;

export function getMyPreferences(): Promise<GetPreferencesResult> {
  return apiClient('/api/v1/notifications/preferences', GetPreferencesResultSchema);
}

export function useMyPreferencesQuery() {
  return useQuery({
    queryKey: ['my-notifications', 'preferences'] as const,
    queryFn: getMyPreferences,
    staleTime: 30_000,
  });
}

export interface PutGlobalInput {
  subscriptions: GlobalSubscriptionEntry[];
}

export function putGlobalPreferences(input: PutGlobalInput): Promise<PutPreferencesResult> {
  return apiClient('/api/v1/notifications/preferences/global', PutPreferencesResultSchema, {
    method: 'PUT',
    body: input,
  });
}

export function usePutGlobalPreferencesMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: putGlobalPreferences,
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['my-notifications', 'preferences'] });
    },
  });
}

export interface PutEngagementInput {
  engagementId: string;
  mutes: Array<{ event_type: string; is_muted: boolean }>;
}

export function putEngagementPreferences(input: PutEngagementInput): Promise<PutPreferencesResult> {
  return apiClient(
    `/api/v1/notifications/preferences/engagement/${encodeURIComponent(input.engagementId)}`,
    PutPreferencesResultSchema,
    { method: 'PUT', body: { mutes: input.mutes } },
  );
}

export function usePutEngagementPreferencesMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: putEngagementPreferences,
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['my-notifications', 'preferences'] });
    },
  });
}

/**
 * Email digest cadence — UI-only enum for v1.
 *
 * **Backend gap (flagged):** the T-0080 preference shape carries
 * `is_subscribed` (global) + `is_muted` (per-engagement) but no
 * `digest_cadence` ('off' | 'daily' | 'weekly'). The screen surfaces
 * the toggle so the firm-admin can express the intent; a follow-up
 * backend ticket extends notification_subscriptions with a cadence
 * column and a matching PUT endpoint. Until then this lives in
 * component-local state only and emits no network call.
 */
export type DigestCadence = 'off' | 'daily' | 'weekly';
